import {
  AggregatedAnswer,
  Histogram,
  SurveyResults,
} from "@/features/survey-results/survey-results-types";
import {
  MultipleChoiceQuestion,
  NumberQuestion,
  OpinionScaleQuestion,
  Question,
  Survey,
  TextQuestion,
  YesNoQuestion,
} from "../survey-types";

class AnsweringService {
  async answerSurvey(survey: Survey): Promise<SurveyResults> {
    const answers = await Promise.all(
      survey.questions.map((question) => this.answerQuestion(question))
    );
    const results: SurveyResults = {
      surveyid: survey.id,
      aggregatedAnswers: answers,
    };
    return results;
  }

  async answerQuestion(question: Question): Promise<AggregatedAnswer> {
    if (question.type === "multiple-choice") {
      return this.answerMultipleChoiceQuestion(question);
    } else if (question.type === "yes-no") {
      return this.answerYesNoQuestion(question);
    } else if (question.type === "opinion-scale") {
      return this.answerOpinionScaleQuestion(question);
    } else if (question.type === "text") {
      return this.answerTextQuestion(question);
    } else if (question.type === "number") {
      return this.answerNumberQuestion(question);
    }
    console.error("Can't randomly answer question of type: " + question.type);
    return {
      questionId: question.id,
      histogram: [],
    };
  }

  async answerMultipleChoiceQuestion(
    question: MultipleChoiceQuestion
  ): Promise<AggregatedAnswer> {
    const answer = {
      questionId: question.id,
      histogram: generateNumbers(question.choices, !question.multipleSelection),
    };
    return answer;
  }

  async answerYesNoQuestion(
    question: YesNoQuestion
  ): Promise<AggregatedAnswer> {
    const answer = {
      questionId: question.id,
      histogram: generateNumbers([{ text: "yes" }, { text: "no" }], true),
    };
    return answer;
  }

  async answerOpinionScaleQuestion(
    question: OpinionScaleQuestion
  ): Promise<AggregatedAnswer> {
    const range = generateRange(question.min || 1, question.max || 5);
    const choices = range.map((value) => ({ text: value.toString() }));
    const answer = {
      questionId: question.id,
      histogram: generateNumbers(choices, true),
    };
    return answer;
  }

  async answerTextQuestion(question: TextQuestion): Promise<AggregatedAnswer> {
    const answer = {
      questionId: question.id,
      textSummary: "Various things...",
    };
    return answer;
  }

  async answerNumberQuestion(
    question: NumberQuestion
  ): Promise<AggregatedAnswer> {
    //TODO handle min and max
    const answer = {
      questionId: question.id,
      histogram: [
        { choice: "1-10", count: Math.floor(Math.random() * 100) },
        { choice: "11-20", count: Math.floor(Math.random() * 100) },
        { choice: "21-30", count: Math.floor(Math.random() * 100) },
        { choice: "31-40", count: Math.floor(Math.random() * 100) },
        { choice: "41-50", count: Math.floor(Math.random() * 100) },
        { choice: "51-60", count: Math.floor(Math.random() * 100) },
        { choice: "61-70", count: Math.floor(Math.random() * 100) },
        { choice: "71-80", count: Math.floor(Math.random() * 100) },
        { choice: "81-90", count: Math.floor(Math.random() * 100) },
        { choice: "91-99", count: Math.floor(Math.random() * 100) },
      ],
    };
    return answer;
  }
}

function generateNumbers(
  choices: { text: string }[],
  mustSumTo100: boolean
): Histogram {
  const totalChoices = choices.length;
  if (!totalChoices) {
    return [];
  }
  let expectedTotal = mustSumTo100 ? 100 : Math.ceil(Math.random() * 300);
  let total = 0;
  const ret: Histogram = [];

  choices.forEach((choice) => {
    const randomPercentage =
      Math.floor(Math.random() * (expectedTotal - total - (totalChoices - 1))) +
      1;
    ret.push({ choice: choice.text, count: randomPercentage });
    total += randomPercentage;
  });

  // Adjust the last choice to ensure total sum is 100
  ret[choices.length - 1].count += expectedTotal - total;

  // Don't let numbers be more than 100, it makes not particular sense but it's a hack to be able to display easily since we don't normalize barcharts now
  ret.map((x) => {
    x.count = Math.min(x.count, 100);
  });

  return ret;
}

function generateRange(min: number, max: number): number[] {
  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    throw new Error("Both min and max must be integers.");
  }
  if (min > max) {
    throw new Error("The min value cannot be greater than the max value.");
  }
  const range: number[] = [];
  for (let i = min; i <= max; i++) {
    range.push(i);
  }
  return range;
}

export const randomAnsweringService = new AnsweringService();
